import { groupBy, pick } from 'lodash';

import { CustomPropertyEntityTypeEnum } from '../../../shared/models/enum/custom-property-entity-type.enum';
import { caseDefaultPropertiesDefinition } from '../../../shared/properties/case-default-properties.definition';
import { companyDefaultPropertiesDefinition } from '../../../shared/properties/company-default-properties.definition';
import { individualDefaultPropertiesDefinition } from '../../../shared/properties/individual-default-properties.definition';
import { Property, PropertyTypeEnum } from '../../../shared/properties/types';

const LABEL_OVERRIDE: Record<string, string> = {
  'case.risk.level': 'Risk',
  'case.template.id': 'Template',
};

export const CASE_PROPERTY_KEYS: (keyof typeof caseDefaultPropertiesDefinition)[] =
  ['name', 'externalId', 'risk.level', 'tagIds', 'template.id'];

export const COMPANY_PROPERTY_KEYS: (keyof typeof companyDefaultPropertiesDefinition)[] =
  [
    'address.city',
    'address.country',
    'address.postalCode',
    'address.region',
    'address.state',
    'address.streetAddress',
    'address.streetAddress2',
    'bankingInformation',
    'classifications',
    'commercialName',
    'country',
    'employerIdentificationNumber',
    'entityLegalForm',
    'legalForm',
    'name',
    'registrationDate',
    'registrationNumber',
    'shareCapital',
    'status',
    'taxIdentificationNumber',
    'type',
    'websiteUrl',
  ];

export const INDIVIDUAL_PROPERTY_KEYS: (keyof typeof individualDefaultPropertiesDefinition)[] =
  [
    'address.city',
    'address.country',
    'address.postalCode',
    'address.region',
    'address.state',
    'address.streetAddress',
    'address.streetAddress2',
    'birthCountry',
    'birthDate',
    'email',
    'firstName',
    'isBeneficialOwner',
    'isBusinessContact',
    'isDelegator',
    'isSignatory',
    'lastName',
    'phoneNumber',
    'position',
    'roles',
  ];

export type ControlProperties = {
  case: Property<PropertyTypeEnum>[];
  company: Property<PropertyTypeEnum>[];
  individual: Property<PropertyTypeEnum>[];
};

const pickControlProperties = (
  properties: Record<string, Property<PropertyTypeEnum>>,
  allowedKeys: string[],
): Property<PropertyTypeEnum>[] => {
  return Object.values(pick(properties, allowedKeys)).map((property) => ({
    ...property,
    label:
      LABEL_OVERRIDE[`${property.mapping.entity}.${property.mapping.key}`] ??
      property.label,
  }));
};

/**
 * Get all available properties for controls, organized by entity type
 */
export const getControlProperties = (
  customProperties: Property<PropertyTypeEnum>[] = [],
): ControlProperties => {
  const groupedCustomProperties = groupBy(
    customProperties,
    (property) => property.mapping.entity,
  );

  return {
    [CustomPropertyEntityTypeEnum.case]: [
      ...pickControlProperties(
        caseDefaultPropertiesDefinition,
        CASE_PROPERTY_KEYS,
      ),
      ...(groupedCustomProperties[CustomPropertyEntityTypeEnum.case] ?? []),
    ],
    [CustomPropertyEntityTypeEnum.company]: [
      ...pickControlProperties(
        companyDefaultPropertiesDefinition,
        COMPANY_PROPERTY_KEYS,
      ),
      ...(groupedCustomProperties[CustomPropertyEntityTypeEnum.company] ?? []),
    ],
    [CustomPropertyEntityTypeEnum.individual]: [
      ...pickControlProperties(
        individualDefaultPropertiesDefinition,
        INDIVIDUAL_PROPERTY_KEYS,
      ),
      ...(groupedCustomProperties[CustomPropertyEntityTypeEnum.individual] ??
        []),
    ],
  };
};
